/** Small presentational pieces shared by the setup pages. */
import { useNavigate } from "react-router-dom";
import { CheckIcon, ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { PrimaryButton, SecondaryButton } from "../../../components/Button";
import { EVENT_SETUP_ANCHOR, OVERVIEW_PATH, STEPS, StepKey, ticketPath } from "./types";
import { SetupCheck } from "./checks";

export const Stepper = ({
  current,
  ticketId,
  problems,
}: {
  current: StepKey;
  /** "new" before the ticket exists: later steps stay disabled. */
  ticketId: number | "new";
  problems: Partial<Record<StepKey, number>>;
}) => {
  const navigate = useNavigate();
  const currentIndex = STEPS.findIndex((s) => s.key === current);
  const isNew = ticketId === "new";
  return (
    <div className="bg-white rounded-md shadow-sm px-5 py-3 flex items-center">
      {STEPS.map((s, i) => {
        const isCurrent = s.key === current;
        const isPast = i < currentIndex;
        const count = problems[s.key] ?? 0;
        const disabled = isNew && !isCurrent;
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none min-w-0">
            <button
              type="button"
              disabled={disabled}
              onClick={() => navigate(ticketPath(ticketId, s.key))}
              className="flex items-center gap-2 group disabled:cursor-not-allowed"
              aria-current={isCurrent ? "step" : undefined}
            >
              <span
                className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                  isCurrent
                    ? "bg-primary-600 border-primary-600 text-white"
                    : isPast && count === 0
                      ? "bg-green-700 border-green-700 text-white"
                      : "bg-white border-gray-300 text-gray-500 group-hover:border-gray-400"
                }`}
              >
                {isPast && count === 0 && !isCurrent ? <CheckIcon className="h-4 w-4" /> : i + 1}
              </span>
              <span className={`text-sm font-medium ${isCurrent || isPast ? "text-gray-900" : "text-gray-500"}`}>
                {s.label}
              </span>
              {count > 0 && (
                <span className="rounded-full bg-amber-100 text-amber-800 text-xs px-2 py-0.5">{count}</span>
              )}
            </button>
            {i < STEPS.length - 1 && <div className="flex-1 h-0.5 bg-gray-200 mx-3" />}
          </div>
        );
      })}
    </div>
  );
};

export const StepFooter = ({
  ticketId,
  back,
  next,
  nextLabel,
  onNext,
  onSave,
  busy,
  left,
}: {
  ticketId: number | "new";
  back?: StepKey | "overview";
  next?: StepKey | "overview";
  nextLabel?: string;
  onNext?: () => void | Promise<void>;
  /** Saves this step only, then returns to the ticket list. Steps whose save is a no-op when clean can pass it as is. */
  onSave?: () => void | Promise<void>;
  busy?: boolean;
  left?: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const go = (target: StepKey | "overview") => navigate(target === "overview" ? OVERVIEW_PATH : ticketPath(ticketId, target));
  return (
    <div className="flex items-center justify-between mt-2">
      <div className="text-xs text-gray-500">{left}</div>
      <div className="flex items-center gap-2">
        {back && <SecondaryButton onClick={() => go(back)}>{back === "overview" ? "Back to tickets" : "Back"}</SecondaryButton>}
        {onSave && (
          <SecondaryButton
            isDisabled={!!busy}
            onClick={async () => {
              try {
                await onSave();
              } catch {
                return; // the step already showed a toast
              }
              go("overview");
            }}
          >
            Save and exit
          </SecondaryButton>
        )}
        {(next || onNext) && (
          <PrimaryButton
            isDisabled={!!busy}
            onClick={async () => {
              try {
                if (onNext) await onNext();
              } catch {
                return; // the step already showed a toast
              }
              if (next) go(next);
            }}
          >
            {nextLabel ?? (next === "overview" ? "Done" : next ? `Continue to ${STEPS.find((s) => s.key === next)?.label}` : "Save")}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

export const CheckRow = ({ check, ticketId }: { check: SetupCheck; ticketId?: number | "new" }) => {
  const navigate = useNavigate();
  const icon =
    check.level === "error" ? (
      <XCircleIcon className="h-5 w-5 text-red-700 shrink-0" />
    ) : check.level === "warning" ? (
      <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0" />
    ) : (
      <CheckIcon className="h-5 w-5 text-green-700 shrink-0" />
    );
  const canFix = check.step && check.level !== "ok" && (check.step === "event" || ticketId != null);
  return (
    <div className="flex items-start gap-3 py-2 border-t border-gray-100 first:border-t-0">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900">{check.title}</div>
        {check.body && <div className="text-sm text-gray-600 break-words">{check.body}</div>}
      </div>
      {canFix && (
        <button
          type="button"
          className="text-sm text-primary-700 hover:text-primary-600 whitespace-nowrap"
          onClick={() => {
            if (check.step === "event") {
              navigate(`${OVERVIEW_PATH}#${EVENT_SETUP_ANCHOR}`);
              setTimeout(() => document.getElementById(EVENT_SETUP_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
            } else {
              navigate(ticketPath(ticketId as number | "new", check.step as StepKey));
            }
          }}
        >
          Fix in {check.step === "event" ? "Event Setup" : STEPS.find((s) => s.key === check.step)?.label}
        </button>
      )}
    </div>
  );
};

export const Card = ({ title, subtitle, children, className = "" }: { title?: string; subtitle?: string; children: React.ReactNode; className?: string }) => (
  <div className={`bg-white rounded-md shadow-sm p-5 flex flex-col gap-4 ${className}`}>
    {(title || subtitle) && (
      <div>
        {title && <div className="text-base font-semibold text-gray-900">{title}</div>}
        {subtitle && <div className="text-sm text-gray-600 mt-0.5">{subtitle}</div>}
      </div>
    )}
    {children}
  </div>
);

export const Chip = ({ children, tone = "gray" }: { children: React.ReactNode; tone?: "gray" | "green" | "red" | "amber" | "blue" }) => {
  const cls = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-100 text-green-800",
    red: "bg-red-100 text-red-800",
    amber: "bg-amber-100 text-amber-800",
    blue: "bg-blue-100 text-blue-800",
  }[tone];
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
};

export const money = (cents: unknown) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
